import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogOut, UserCircle } from 'lucide-react';
import { useLogout } from '@/api/auth';
import { Avatar, Badge } from '@/components/ui';
import { humanize } from '@/lib/format';
import { useAuth } from '../auth/useAuth';

export function UserMenu() {
  const { user, workspace } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const logout = useLogout();
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === 'Escape' : !ref.current?.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="hover:ring-brand-100 flex items-center gap-2 rounded-full p-0.5 hover:ring-2"
      >
        <Avatar name={user.name} src={user.avatarUrl} size="sm" />
        <span className="sr-only">Account menu</span>
      </button>
      {open && (
        <div
          role="menu"
          className="shadow-pop absolute right-0 mt-2 w-64 rounded-xl border border-slate-200 bg-white p-1.5"
        >
          <div className="px-3 py-2.5">
            <p className="truncate text-sm font-semibold text-slate-900">{user.name}</p>
            <p className="truncate text-xs text-slate-500">{user.email}</p>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <Badge tone="brand">{humanize(user.role)}</Badge>
              {workspace && <span className="truncate text-xs text-slate-500">· {workspace.name}</span>}
            </div>
          </div>
          <div className="my-1 h-px bg-slate-100" />
          {workspace && (
            <Link
              role="menuitem"
              to="/settings/profile"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              <UserCircle className="h-4 w-4" /> My profile
            </Link>
          )}
          <button
            role="menuitem"
            type="button"
            onClick={() =>
              logout.mutate(undefined, { onSettled: () => navigate('/login', { replace: true }) })
            }
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}
